import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import PublicTrack from "@/pages/PublicTrack";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import DashboardLayout from "@/layouts/DashboardLayout";
import Overview from "@/pages/dashboard/Overview";
import AddOrder from "@/pages/dashboard/AddOrder";
import Orders from "@/pages/dashboard/Orders";
import Rates from "@/pages/dashboard/Rates";
import Tracking from "@/pages/dashboard/Tracking";
import Wallet from "@/pages/dashboard/Wallet";
import Warehouses from "@/pages/dashboard/Warehouses";
import Cod from "@/pages/dashboard/Cod";
import Kyc from "@/pages/dashboard/Kyc";
import Ndr from "@/pages/dashboard/Ndr";
import WeightReview from "@/pages/dashboard/WeightReview";
import TruckBooking from "@/pages/dashboard/TruckBooking";
import Tickets from "@/pages/dashboard/Tickets";
import Settings from "@/pages/dashboard/Settings";
import Tracker from "@/pages/dashboard/Tracker";
import Reseller from "@/pages/dashboard/Reseller";
import Invoices from "@/pages/dashboard/Invoices";
import RateCard from "@/pages/dashboard/RateCard";
import AdminOverview from "@/pages/admin/AdminOverview";
import Merchants from "@/pages/admin/Merchants";
import MerchantDetail from "@/pages/admin/MerchantDetail";
import KycQueue from "@/pages/admin/KycQueue";
import AdminCod from "@/pages/admin/AdminCod";
import AdminOrders from "@/pages/admin/AdminOrders";
import Trucks from "@/pages/admin/Trucks";
import AdminTickets from "@/pages/admin/AdminTickets";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminWarehouses from "@/pages/admin/AdminWarehouses";
import AdminWeight from "@/pages/admin/AdminWeight";
import AdminLabels from "@/pages/admin/AdminLabels";
import AdminInvoices from "@/pages/admin/AdminInvoices";

const BootScreen = () => (
  <div className="min-h-screen bg-paper flex items-center justify-center" data-testid="auth-loading">
    <span className="font-mono text-xs tracking-[0.3em] uppercase text-obsidian">
      Connecting to ShipZilla…
    </span>
  </div>
);

const MerchantRoute = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <BootScreen />;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <BootScreen />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/track" element={<PublicTrack />} />
            <Route path="/track/:awb" element={<PublicTrack />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route
              path="/dashboard"
              element={
                <MerchantRoute>
                  <DashboardLayout role="merchant" />
                </MerchantRoute>
              }
            >
              <Route index element={<Overview />} />
              <Route path="add-order" element={<AddOrder />} />
              <Route path="orders" element={<Orders />} />
              <Route path="rates" element={<Rates />} />
              <Route path="tracking" element={<Tracking />} />
              <Route path="tracker" element={<Tracker />} />
              <Route path="ratecard" element={<RateCard />} />
              <Route path="reseller" element={<Reseller />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="ndr" element={<Ndr />} />
              <Route path="weight" element={<WeightReview />} />
              <Route path="trucks" element={<TruckBooking />} />
              <Route path="wallet" element={<Wallet />} />
              <Route path="warehouses" element={<Warehouses />} />
              <Route path="cod" element={<Cod />} />
              <Route path="kyc" element={<Kyc />} />
              <Route path="tickets" element={<Tickets />} />
              <Route path="settings" element={<Settings />} />
            </Route>
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <DashboardLayout role="admin" />
                </AdminRoute>
              }
            >
              <Route index element={<AdminOverview />} />
              <Route path="merchants" element={<Merchants />} />
              <Route path="merchants/:id" element={<MerchantDetail />} />
              <Route path="kyc" element={<KycQueue />} />
              <Route path="cod" element={<AdminCod />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="trucks" element={<Trucks />} />
              <Route path="warehouses" element={<AdminWarehouses />} />
              <Route path="weight" element={<AdminWeight />} />
              <Route path="labels" element={<AdminLabels />} />
              <Route path="invoices" element={<AdminInvoices />} />
              <Route path="tickets" element={<AdminTickets />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            style: { borderRadius: 0, border: "2px solid #0B1E3A", fontFamily: "'Plus Jakarta Sans', sans-serif" },
          }}
        />
      </AuthProvider>
    </div>
  );
}

export default App;
